import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { first } from 'rxjs/operators';
import { appInjector } from '../../app-injector';
import { Dictionary } from '../../models/dictionary.model';
import { IRoute } from '../../models/iroute.interface';
import { RouteModel } from '../../models/route.model';
import { ValidationModel } from '../../models/validation-results/validation.model';
import { WorkflowInstanceModel } from '../../models/workflow/workflow-instance.model';
import { WorkflowService } from '../workflow/workflow.service';

declare var webserviceUrl: String;

@Injectable()
export class NavigationService {
    public navigatedFoward$: Subject<IRoute> = new Subject<IRoute>();
    public navigatedBackward$: Subject<IRoute> = new Subject<IRoute>();
    public navigatedToRoute$: Subject<IRoute> = new Subject<IRoute>();
    public validated$: Subject<any> = new Subject<any>();
    public checkValidation$: Subject<any> = new Subject<any>();
    public feesCalculated$: Subject<any> = new Subject<any>();
    public valueChanged$: Subject<Dictionary> = new Subject<Dictionary>();
    public businessTransactionId: number;
    public transactionTypeId: number;
    public fees: any;
    public validationResults: any;
    public configurationValues: Dictionary;
    public workflowInstance: WorkflowInstanceModel;
    private position: number;
    private routes: IRoute[];
    private workflowService: WorkflowService;

    getWorkflowService(): WorkflowService {
        if (!this.workflowService) {
            var injector = appInjector();
            this.workflowService = <WorkflowService> injector.get(WorkflowService);
        }
        return this.workflowService;
    }

    constructor() {
        this.validationResults = [];
        this.routes = new Array<RouteModel>();
        this.configurationValues = new Dictionary([]);
        this.workflowInstance = new WorkflowInstanceModel();
    }

    /**
     * Clears the internal configuration values for the navigation service.
     */
    clearValues(): void {
        this.businessTransactionId = null;
        this.transactionTypeId = null;
        this.configurationValues = new Dictionary([]);
        this.clearValidation();
    }

    /**
     * Increments the position in the route list.
     */
    private incrementPosition() {
        if (this.position < this.routes.length) {
            this.position = this.position + 1;
        }
    }

    /**
     * Decrements the position in the route list.
     */
    private decrementPosition() {
        if (this.position > 0) {
            this.position = this.position - 1;
        }
    }

    /**
     * Sets the position of the navigation in the routes.
     * NOTE: THIS SHOULD ONLY BE CALLED IF THE POSITION MUST ABSOLUTELY BE OVERWRITTEN
     * @param pos: Position ordinal to set.
     */
    setCurrentPosition(pos: number): void {
        this.position = pos;
    }

    /**
     * Removes all routes in the array after the current position.
     */
    removeRoutesAfterCurrentPosition(): void {
        if (this.routes.length > 1 && this.position + 1 < this.routes.length) {
            this.routes.splice(this.position + 1, this.routes.length - this.position - 1);
        }
    }

    /**
     * Removes the routes given from the route array.
     * @param routesToRemove: Array of routes to remove.
     */
    removeRoutes(routesToRemove: IRoute[]): void {
        if (routesToRemove.length > 0 && this.routes.length > 0) {
            for (var i = 0; i < routesToRemove.length; i++) {
                const index: number = this.routes.findIndex(r => r.routeName === routesToRemove[i].routeName && r.transactionTypeId === routesToRemove[i].transactionTypeId);
                this.routes.splice(index, 1);
            }
        }
    }

    /**
     * Appends a list of routes to the end of the current route list.
     * @param routes: List of routes to append.
     */
    appendRoutes(routes: IRoute[]): void {
        for (var i = 0; i < routes.length; i++) {
            this.routes.push(routes[i]);
        }
    }

    /**
     * Replaces the next route in the list with the new route.
     * @param newRoute: Route that will replace the old route.
     */
    replaceNextRoute(newRoute: IRoute): void {
        if (this.position + 1 < this.routes.length) {
            this.routes[this.position + 1] = newRoute;
        }
    }

    setFees(newFees: any) {
        this.fees = newFees;
        this.feesCalculated$.next(this.fees);
    }

    /**
     * Loads the list of routes.  This will replace any previous routes already loaded.
     * @param routeList: List of routes to load.
     */
    loadRoutes(routeList: IRoute[]): void {
        this.routes = routeList;

        if (routeList.length > 1) {
            this.setCurrentPosition(1);
        } else {
            this.setCurrentPosition(0);
        }
    }

    /**
     * Gets the current position in the route list.
     */
    getCurrentPosition(): number {
        return this.position;
    }

    /**
     * Gets the current route that has been navigated to.
     */
    getCurrentRoute(): IRoute {
        return this.getRouteAtPosition(this.position);
    }

    /**
     * Gets the previous route in the route list.
     */
    getPreviousRoute(): IRoute {
        return this.getRouteAtPosition(this.position - 1);
    }

    /**
     * Gets the next route in the route list.
     */
    getNextRoute(): IRoute {
        return this.getRouteAtPosition(this.position + 1);
    }

    /**
     * Gets the route in the list at the specified position.
     * @param position: Position in the list to get.
     */
    getRouteAtPosition(position: number): IRoute {
        if (this.routes.length >= position) {
            return this.routes[position];
        }

        return null;
    }

    /**
     * Gets the index of the given route in the list.
     * @param route: Route to find in the list.
     */
    getRoutePosition(route: IRoute): number {
        return this.routes.indexOf(route);
    }

    /**
     * Gets all the routes currently loaded.
     * @returns: Array of all loaded routes.
     */
    getAllRoutes(): IRoute[] {
        return this.routes;
    }

    /**
     * Inserts the route at the current position in the navigation list.
     * @param route: Route to insert into the list.
     */
    insertRouteAtCurrentPosition(route: IRoute): void {
        this.insertRouteAtPosition(this.position, route);
    }

    /**
     * Determines whether or not the route list contains the specified route.
     * @param route: Name of the route to check for.
     * @returns: True if the route list contains the route.  Otherwise, false.
     */
    containsRoute(route: string): boolean {
        if (this.routes) {
            const foundRoute: IRoute = this.routes.find((r) => r.routeName === route);
            if (foundRoute) {
                return true;
            }
        }

        return false;
    }

    /**
     * Gets all routes that have the breadcrumbName property set.
     */
    getTransactionalRoutes(): IRoute[] {
        const foundRoutes: IRoute[] = this.routes.filter(r => r.breadcrumbName && r.breadcrumbName !== null);
        if (foundRoutes) {
            return foundRoutes;
        }

        return new Array<IRoute>();
    }

    notifyValueChanged(valueName: string, value: number): void {
        const values: Dictionary = new Dictionary([]);
        values.add(valueName, value);
        this.valueChanged$.next(values);
    }

    /**
     * Navigates the transaction forward.  This will attempt to run fee calculation and/or
     * validation if those functions have been configured for the current route.
     */
    navigateForward(): void {
        const self = this;
        const currentRoute: IRoute = this.getRouteAtPosition(this.position);
        this.clearValidation();
        this.fees = [];

        if (currentRoute.savableComponent) {
            currentRoute.savableComponent.save().pipe(first()).subscribe(
                r => {
                    if (r) {
                        self.evaluateForwardMove(currentRoute);
                    }
                },
                e => {
                    console.log(e);
                }
            );
        } else {
            this.evaluateForwardMove(currentRoute);
        }
    }

    /**
     * Navigates the transaction backward.
     */
    navigateBackward(): void {
        this.decrementPosition();
        this.clearValidation();
        this.navigatedBackward$.next(this.getRouteAtPosition(this.position));
    }

    /**
     * Navigates the transaction to the specified route.
     * @param route: Route to navigate to.
     */
    navigateToRoute(route: IRoute): void {
        const routeIndex: number = this.getRoutePosition(route);
        this.position = routeIndex;
        this.clearValidation();
        this.navigatedToRoute$.next(route);
    }

    /**
     * Navigates back to the home page.
     */
    navigateHome(): void {
        this.clearValues();
        this.clearValidation();
        this.navigatedToRoute$.next(this.getRouteAtPosition(0));
    }

    /**
     * Clears the validation errors.
     */
    clearValidation(): void {
        const errors = [];
        this.validated$.next(errors);
        this.checkValidation$.next(false);
    }

    /**
     * Inserts a route at a given position in the route list.
     * @param position: Position in the list to insert the route.
     * @param route: The route to insert.
     */
    private insertRouteAtPosition(position: number, route: IRoute): void {
        this.routes.splice(position, 0, route);
    }

    /**
     * Evaluates the actions required before the transaction is allowed to move to the next route, based
     * on the configured items for the current route.
     * @param currentRoute: Route that has the delegate calls to be invoked.
     */
    private evaluateForwardMove(currentRoute: IRoute): void {
        const self = this;
        if (currentRoute.feeCalculation) {
            this.runFeeCalculation(currentRoute);
        } else if (currentRoute.validation) {
            self.runValidationAndNavigateForward(currentRoute);
        } else if (currentRoute.cartContext) {
            currentRoute.cartContext.addCartItem(this.businessTransactionId).pipe(first()).subscribe(
                r => {
                    // self.incrementPositionAndNavigateForward();
                    self.updateWorkflowInstance();
                }, e => {
                    console.log(e);
                }
            );
        } else if (currentRoute.transactionCreation) {
            this.createTransaction(currentRoute).pipe(first()).subscribe(
                r => {
                    if (currentRoute.workflowCreation) {
                        this.createWorkFlowInstance(currentRoute, currentRoute.transactionTypeId);
                    } else {
                        self.updateWorkflowInstance();
                    }
                },
                err => {
                    console.log(err);
                }
            );
        } else {
            self.updateWorkflowInstance();
            // self.incrementPositionAndNavigateForward();
        }
    }

    /**
     * Runs the configured fee calculation delegate for the given route.
     * @param currentRoute: Current route of the transaction.
     */
    private runFeeCalculation(currentRoute: IRoute): void {
        const self = this;
        currentRoute.feeCalculation(currentRoute.context).pipe(first()).subscribe(
            r => {
                self.fees = r;
                self.feesCalculated$.next(self.fees);
            },
            e => {
                console.log(e);
            },
            () => {
                if (currentRoute.validation) {
                    self.runValidationAndNavigateForward(currentRoute);
                } else {
                    self.updateWorkflowInstance();
                    // self.incrementPositionAndNavigateForward();
                }
            }
        );
    }

    /**
     * Runs the configured validation for the specified route.  If successful, the transaction will be
     * navigated forward.
     * @param currentRoute: The route to validate.
     */
    private runValidationAndNavigateForward(currentRoute: IRoute): void {
        const self = this;
        currentRoute.validation(currentRoute.context).pipe(first()).subscribe(
            r => {
                let validationModel;
                if (r._body) {
                    var results = new ValidationModel(r);
                    validationModel = results;
                } else {
                    validationModel = r;
                }

                if (validationModel.validationResults.length === 0) {
                    self.validated$.next([]);
                    self.checkValidation$.next(false);
                    if (currentRoute.transactionCreation) {
                        self.createTransaction(currentRoute).pipe(first()).subscribe(
                            r => {
                                if (currentRoute.workflowCreation) {
                                    this.createWorkFlowInstance(currentRoute, currentRoute.transactionTypeId);
                                } else {
                                    self.updateWorkflowInstance();
                                }

                                // self.incrementPositionAndNavigateForward();

                            },
                            err => {
                                console.log(err);
                            }
                        );
                    } else {
                        // self.incrementPositionAndNavigateForward();
                        self.updateWorkflowInstance();
                    }
                } else {
                    self.validated$.next(validationModel);
                    self.checkValidation$.next(true);
                }
            },
            e => {
                var results = new ValidationModel(e);
                self.validated$.next(results);
                self.checkValidation$.next(true);
            },
            () => {
            }
        );
    }

    /**
     * Attempts to create the transaction using the given creation delegate.
     * @param currentRoute: Route that has the creation delegate.
     * @returns: Observable for when the creation attempt is completed.
     */
    private createTransaction(currentRoute: IRoute): Observable<boolean> {
        const self = this;
        const createSubject$: Subject<boolean> = new Subject<boolean>();

        if (currentRoute.transactionCreation) {
            if (currentRoute.transactionCreationParameter) {
                currentRoute.transactionCreation(currentRoute.transactionCreationParameter).pipe(first()).subscribe(
                    r => {
                        currentRoute.transactionObject = r;
                        currentRoute.transactionService.saveTransaction(currentRoute.transactionObject, currentRoute.transactionTypeId).pipe(first()).subscribe(
                            result => {
                                self.businessTransactionId = +result;
                                self.transactionTypeId = currentRoute.transactionTypeId;
                                createSubject$.next(true);
                            },
                            err => {
                                console.log(err);
                                createSubject$.next(false);
                            }
                        );
                    },
                    e => {
                        console.log(e);
                        createSubject$.next(false);
                    },
                    () => {
                    }
                );
            } else {
                currentRoute.transactionCreation(currentRoute.transactionContext).pipe(first()).subscribe(
                    r => {
                        currentRoute.transactionObject = r;
                        currentRoute.transactionService.saveTransaction(currentRoute.transactionObject, currentRoute.transactionTypeId).pipe(first()).subscribe(
                            result => {
                                self.businessTransactionId = +result;
                                self.transactionTypeId = currentRoute.transactionTypeId;
                                createSubject$.next(true);
                            },
                            err => {
                                console.log(err);
                                createSubject$.next(false);
                            }
                        );
                    },
                    e => {
                        console.log(e);
                        createSubject$.next(false);
                    },
                    () => {
                    }
                );
            }
        } else {
            createSubject$.next(false);
        }

        return createSubject$.asObservable();
    }

    private createWorkFlowInstance(currentRoute: IRoute, transactionTypeId: number): void {
        const self = this;

        if (currentRoute.workflowCreation) {
            if (transactionTypeId === 12 || transactionTypeId === 5) {
                this.getWorkflowService().startWorkflowForBusinessTransaction(self.businessTransactionId).subscribe(
                    r => {
                        currentRoute.transactionObject.workflowInstance = self.workflowInstance;
                        currentRoute.transactionObject.workflowInstance.id = r;
                        this.updateWorkflowInstance();
                    },
                    err => {
                        console.log(err);
                    }
                );
            }
        }
    }

    private getReadyStepsForWorkflowInstance(currentRoute: IRoute, workflowInstanceId: number) {
        const self = this;
        this.getWorkflowService().getReadySteps(workflowInstanceId).subscribe(
            result => {
                self.workflowInstance.readySteps = result;
            },
            e => {
                console.log(e);
            }
        );
    }

    private startStepForWorkflowInstance(workflowInstanceId: number, workflowStepId: number) {
        this.getWorkflowService().startStep(workflowInstanceId, workflowStepId).subscribe(
            result => {
            },
            e => {
                console.log(e);
            }
        );
    }

    private completeStepForWorkflowInstance(workflowInstanceId: number, workflowStepId: number) {
        this.getWorkflowService().completeStep(workflowInstanceId, workflowStepId).subscribe(
            result => {
            },
            e => {
                console.log(e);
            }
        );
    }

    nextStep(): void {
        const self = this;
        this.getWorkflowService().getReadySteps(self.workflowInstance.id).subscribe(
            result => {
                self.workflowInstance.readySteps = result;
                if (result) {
                    var readyWorkflowSteps = self.workflowInstance.readySteps;
                    // var currentPosition = this.getCurrentPosition();
                    // var nextPosition = currentPosition;
                    // var nonTransactionRoutes = this.getAllRoutes().filter(r => !r.breadcrumbName);
                    var routes = this.getAllRoutes();
                    //  var transactionRoutes = routes.filter(b => (b.breadcrumbName && b.breadcrumbName.length > 0));
                    //  var previousRoute = this.getPreviousRoute();
                    //  if (!currentRoute.breadcrumbName) {
                    //       nextPosition = ++currentPosition;
                    //  }
                    if (readyWorkflowSteps.length === 1 && routes.filter(r => (r.breadcrumbName && r.breadcrumbName === readyWorkflowSteps[0].workflowStepName)).length > 0) {
                        var nextWorkflowStep = readyWorkflowSteps[0];
                        this.getWorkflowService().startStep(self.workflowInstance.id, nextWorkflowStep.id).subscribe(
                            response => {
                                this.incrementPositionAndNavigateForward();
                            },
                            error => {
                                console.log(error);
                            }
                        );
                    } else if (readyWorkflowSteps.length === 1 && routes.filter(r => r.routeName.substr(1, r.routeName.length) === readyWorkflowSteps[0].workflowStepName).length > 0) {
                        var nextWorkflowStep = readyWorkflowSteps[0];
                        this.getWorkflowService().startStep(self.workflowInstance.id, nextWorkflowStep.id).subscribe(
                            response => {
                                this.incrementPositionAndNavigateForward();
                                // this.updateWorkflowInstance();
                            },
                            error => {
                                console.log(error);
                            }
                        );
                    } else {
                        this.incrementPositionAndNavigateForward();
                    }
                } else {
                    this.incrementPositionAndNavigateForward();
                }
            },
            e => {
                console.log(e);
            }
        );
    }

    updateWorkflowInstance(): void {
        const self = this;
        const currentRoute: IRoute = this.getRouteAtPosition(this.position);
        var routeName = currentRoute.routeName.substr(1, currentRoute.routeName.length);
        if (self.businessTransactionId) {
            if (this.transactionTypeId !== 12 && this.transactionTypeId !== 5) {
                this.incrementPositionAndNavigateForward();
            } else {
                if (self.workflowInstance) {
                    let currentStep = null;

                    if (self.workflowInstance.readySteps) {
                        for (let i = 0; i < self.workflowInstance.readySteps.length; i++) {
                            const readyStep = self.workflowInstance.readySteps[i];

                            if (readyStep.workflowStepName === currentRoute.breadcrumbName ) {
                                currentStep = readyStep;
                            } else if (readyStep.workflowStepName === routeName) {
                                currentStep = readyStep;
                            }
                        }
                    }

                    if (currentStep) {
                        this.getWorkflowService().completeStep(self.workflowInstance.id, currentStep.id).subscribe(r => {
                            self.nextStep();
                        }
                        );
                    } else {
                        self.nextStep();
                    }
                } else {
                    this.getWorkflowService().startWorkflowForBusinessTransaction(self.businessTransactionId).subscribe(
                        r => {
                            self.workflowInstance = self.workflowInstance;
                            self.workflowInstance.id = r;
                            this.getWorkflowService().getReadySteps(r).subscribe(
                                result => {
                                    self.workflowInstance.readySteps = result;
                                    if (result) {
                                        var readyWorkflowSteps = self.workflowInstance.readySteps;
                                        var routes = this.getAllRoutes();
                                        if (readyWorkflowSteps.length === 1 && routes.filter(r => (r.breadcrumbName && r.breadcrumbName === readyWorkflowSteps[0].workflowStepName)).length > 0) {
                                            var nextWorkflowStep = readyWorkflowSteps[0];
                                            this.getWorkflowService().startStep(self.workflowInstance.id, nextWorkflowStep.id).subscribe(
                                                response => {
                                                    this.incrementPositionAndNavigateForward();
                                                },
                                                error => {
                                                    console.log(error);
                                                }
                                            );
                                        }
                                    } else {
                                        this.incrementPositionAndNavigateForward();
                                    }
                                },
                                e => {
                                    console.log(e);
                                }
                            );
                        },
                        err => {
                            console.log(err);
                        }
                    );
                }
            }
        } else {
            this.incrementPositionAndNavigateForward();
        }
    }

    /**
     * Moves the position in the navigation and emits the event to move to the next route.
     */
    private incrementPositionAndNavigateForward(): void {
        const self = this;
        self.incrementPosition();
        self.navigatedFoward$.next(self.getRouteAtPosition(self.position));
    }
}
